import React, { useState, useEffect } from 'react';
import { FaUpload, FaCheckCircle, FaTimesCircle, FaClock, FaIdCard, FaFileAlt } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';

const KYCPage = () => {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    panNumber: '',
    aadharNumber: '',
    panCard: null,
    aadharFront: null,
    aadharBack: null,
    passportPhoto: null,
    bankStatement: null
  });
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/kyc/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKycStatus(response.data.kyc);
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setFormData({ ...formData, [fieldName]: file });
      setPreviews({ ...previews, [fieldName]: URL.createObjectURL(file) });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.panNumber || !formData.aadharNumber) {
      toast.error('Please enter PAN and Aadhar numbers');
      return;
    }
    
    if (!formData.panCard || !formData.aadharFront || !formData.aadharBack || !formData.passportPhoto) {
      toast.error('Please upload all required documents');
      return;
    }
    
    setSubmitting(true);
    const data = new FormData();
    data.append('panNumber', formData.panNumber);
    data.append('aadharNumber', formData.aadharNumber);
    data.append('panCard', formData.panCard);
    data.append('aadharFront', formData.aadharFront);
    data.append('aadharBack', formData.aadharBack);
    data.append('passportPhoto', formData.passportPhoto);
    if (formData.bankStatement) {
      data.append('bankStatement', formData.bankStatement);
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/kyc/submit`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('KYC documents submitted successfully!');
      fetchKYCStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = () => {
    if (!kycStatus) {
      return { text: 'Not Submitted', color: 'bg-gray-100 text-gray-800', icon: <FaFileAlt /> };
    }
    switch (kycStatus.status) {
      case 'pending':
        return { text: 'Under Review', color: 'bg-yellow-100 text-yellow-800', icon: <FaClock /> };
      case 'approved':
        return { text: 'Verified', color: 'bg-green-100 text-green-800', icon: <FaCheckCircle /> };
      case 'rejected':
        return { text: 'Rejected', color: 'bg-red-100 text-red-800', icon: <FaTimesCircle /> };
      default:
        return { text: 'Not Submitted', color: 'bg-gray-100 text-gray-800', icon: <FaFileAlt /> };
    }
  };

  const status = getStatusBadge();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>KYC Verification - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">KYC Verification</h1>
          <p className="text-gray-500 mb-6">Complete your KYC to start investing</p>

          {/* Status Card */}
          <div className={`${status.color} rounded-xl p-4 mb-6 flex items-center gap-3`}>
            {status.icon}
            <span className="font-semibold">KYC Status: {status.text}</span>
          </div>

          {kycStatus?.status === 'approved' ? (
            <div className="bg-green-50 rounded-xl p-8 text-center border border-green-200">
              <FaCheckCircle className="text-green-600 text-5xl mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">KYC Verified!</h2>
              <p className="text-gray-600">Your KYC has been verified. You can now make investments.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-6 space-y-5">
              {/* PAN Card */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PAN Card Number *</label>
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  placeholder="ABCDE1234F"
                />
              </div>

              {/* Aadhar Card */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Card Number *</label>
                <input
                  type="text"
                  name="aadharNumber"
                  value={formData.aadharNumber}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500"
                  placeholder="1234 5678 9012"
                />
              </div>

              {/* PAN Card Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PAN Card Image *</label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500" onClick={() => document.getElementById('panCard').click()}>
                  <FaUpload className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">Click to upload PAN card</p>
                </div>
                <input id="panCard" type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'panCard')} className="hidden" />
                {previews.panCard && <img src={previews.panCard} alt="PAN Card" className="mt-2 h-20 object-cover rounded" />}
              </div>

              {/* Aadhar Front */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Card (Front) *</label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500" onClick={() => document.getElementById('aadharFront').click()}>
                  <FaUpload className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">Click to upload Aadhar front</p>
                </div>
                <input id="aadharFront" type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'aadharFront')} className="hidden" />
                {previews.aadharFront && <img src={previews.aadharFront} alt="Aadhar Front" className="mt-2 h-20 object-cover rounded" />}
              </div>

              {/* Aadhar Back */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Card (Back) *</label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500" onClick={() => document.getElementById('aadharBack').click()}>
                  <FaUpload className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">Click to upload Aadhar back</p>
                </div>
                <input id="aadharBack" type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'aadharBack')} className="hidden" />
                {previews.aadharBack && <img src={previews.aadharBack} alt="Aadhar Back" className="mt-2 h-20 object-cover rounded" />}
              </div>

              {/* Passport Photo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Passport Size Photo *</label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500" onClick={() => document.getElementById('passportPhoto').click()}>
                  <FaUpload className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">Click to upload photo</p>
                </div>
                <input id="passportPhoto" type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'passportPhoto')} className="hidden" />
                {previews.passportPhoto && <img src={previews.passportPhoto} alt="Photo" className="mt-2 h-20 object-cover rounded" />}
              </div>

              {/* Bank Statement (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Statement (Optional)</label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-green-500" onClick={() => document.getElementById('bankStatement').click()}>
                  <FaUpload className="mx-auto h-8 w-8 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">Click to upload bank statement</p>
                </div>
                <input id="bankStatement" type="file" accept="image/*,application/pdf" onChange={(e) => handleFileChange(e, 'bankStatement')} className="hidden" />
                {previews.bankStatement && <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit KYC'}
              </button>

              <div className="p-3 bg-yellow-50 rounded-lg text-xs text-yellow-800">
                <strong>Note:</strong> KYC verification may take 24-48 hours. You can invest only after KYC approval.
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default KYCPage;